import React, { useState} from "react";
import { useAuth } from '@/shared/hooks';
import { useNavigate } from "react-router-dom";
import { logoutUser } from '@/features/auth/api';
import type { User } from '@/shared/types';

  interface ProfileMenuProps {
    user: User | null;
    profileHovered: boolean;
    showChangeProfileSettingsModal: React.Dispatch<React.SetStateAction<string | null>>;
  }

  interface LogoutResult {
	success: boolean;
	error?:	string | undefined;
}

  function ProfileMenu({ user, profileHovered, showChangeProfileSettingsModal }: ProfileMenuProps) {
    const navigate = useNavigate();
    const [logoutConfirmation, showLogoutConfirmation] = useState(false);
    const [error, setError] = useState<string | null>(null);
	  const [logoutStatus, setLogoutStatus] = useState<LogoutResult>({ success: false, error: undefined });
    const { firebaseUser, loading } = useAuth();

    const Logout = async () => {
      setError(null);
      setLogoutStatus({ success: false, error: undefined });

      if( loading || !firebaseUser ){
        setLogoutStatus({ success: false, error: "Not logged in to log out." });
        return
      }
  
      try {
        const status = await logoutUser(firebaseUser, navigate);
        setLogoutStatus(status);

        if (!status.success){
            setError(status.error || "Logout failed");
            return;
        }

        // Auth state will be cleared automatically by AuthContext when Firebase signs out
        // No need to refetch - the onAuthStateChanged listener handles this

      } catch (error) {
            console.error("Error logging user out", error);
      }
    };

    if (!user || !profileHovered) return null;

    return (
        <div
              style={{
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  padding: '10px',
                  minWidth: '150px',
                  color: '#000'
              }}
          >
              <div style={{ padding: '8px 0px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width={15} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 9v.906a2.25 2.25 0 0 1-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 0 0 1.183 1.981l6.478 3.488m8.839 2.51-4.66-2.51m0 0-1.023-.55a2.25 2.25 0 0 0-2.134 0l-1.022.55m0 0-4.661 2.51m16.5 1.615a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V8.844a2.25 2.25 0 0 1 1.183-1.981l7.5-4.039a2.25 2.25 0 0 1 2.134 0l7.5 4.039a2.25 2.25 0 0 1 1.183 1.98V19.5Z" />
                </svg>

                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                  <div>
                    <div style={{ height: '17px'}}>
                    Email
                    </div>
                    <div data-cy="email" style={{ fontSize: '11px', fontWeight: '400', width: '155px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {user.email}
                    </div>
                  </div>
                  <button data-cy="change-email" style={{ fontSize: '11px', textDecoration: 'underline', marginLeft: '10px', cursor: 'pointer' }} onClick={async (e) => { e.preventDefault(); await showChangeProfileSettingsModal('Email') }}>
                    Change
                  </button>
                </div>
              </div>
              <div style={{ padding: '8px 0px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width={15} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 0 1-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004ZM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 0 1-.921.42Z" />
                  <path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v.816a3.836 3.836 0 0 0-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 0 1-.921-.421l-.879-.66a.75.75 0 0 0-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 0 0 1.5 0v-.81a4.124 4.124 0 0 0 1.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 0 0-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 0 0 .933-1.175l-.415-.33a3.836 3.836 0 0 0-1.719-.755V6Z" clip-rule="evenodd" />
                </svg>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                  <div>
                    <div style={{ height: '17px'}}>
                      Balance
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: '400' }}>
                      ${user.balance}
                    </div>
                  </div>
                </div>
              </div>
              <button data-cy="logout" onClick={() => showLogoutConfirmation(true)}>
                <div style={{ padding: '8px 0px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width={15} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/></svg>
                  {logoutConfirmation
                  ?
                    <div style={{display: 'flex', gap: '8px' }}>
                      Are you sure?
                      <div className="confirmationButtonsContainer" style={{ display: 'flex' , paddingTop: '0px', width: 'auto'}}>
                        <button
                          onClick={() => showLogoutConfirmation(false)}
                          className="cancelButton"
                          style={{ padding: '0px 12px'}}
                        >
                          No
                        </button>
                        <button
                          onClick={() => {
                            showLogoutConfirmation(false);
                            Logout();
                          }}
                          data-cy={`logout-confirmation`}
                          className="progressButton"
                          style={{ padding: '0px 12px'}}
                        >
                          Yes
                        </button>
                      </div>
                    </div>
                    :
                    "Logout"
                  }
                </div>
              </button>
          </div>
    )
}

export default ProfileMenu;